import React, { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";

interface RotatingTextProps {
  words: string[];
  active: boolean;
  onBoundary?: () => void;
  showCursor?: boolean;
  typeSpeedMs?: number;
  deleteSpeedMs?: number;
  pauseMs?: number;
}

const RotatingText: React.FC<RotatingTextProps> = ({
  words,
  active,
  onBoundary,
  showCursor = false,
  typeSpeedMs = 55,
  deleteSpeedMs = 32,
  pauseMs = 1400,
}) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<"typing" | "pausing" | "deleting">(
    "typing",
  );

  useEffect(() => {
    const currentWord = words[wordIndex % words.length];
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (phase === "typing") {
      if (text.length < currentWord.length) {
        timer = setTimeout(
          () => setText(currentWord.slice(0, text.length + 1)),
          typeSpeedMs,
        );
      } else if (active) {
        timer = setTimeout(() => setPhase("pausing"), pauseMs);
      }
      // fully typed but not the active slot: hold steady, no timer scheduled
    } else if (phase === "pausing") {
      if (active) {
        timer = setTimeout(() => setPhase("deleting"), 0);
      }
      // inactive: stay paused indefinitely until this becomes the active slot
    } else if (active) {
      if (text.length > 0) {
        timer = setTimeout(() => setText(text.slice(0, -1)), deleteSpeedMs);
      } else {
        // clean boundary: safe moment to hand the active slot to someone else
        timer = setTimeout(() => {
          setWordIndex((prev) => (prev + 1) % words.length);
          setPhase("typing");
          onBoundary?.();
        }, 250);
      }
    }

    return () => clearTimeout(timer);
  }, [
    text,
    phase,
    wordIndex,
    words,
    active,
    typeSpeedMs,
    deleteSpeedMs,
    pauseMs,
    onBoundary,
  ]);

  return (
    <Box as="span" display="inline-flex" alignItems="center">
      <Box as="span">{text}</Box>
      {showCursor && active && (
        <Box
          as="span"
          display="inline-block"
          w="3px"
          h="0.85em"
          bg="currentColor"
          ml={2}
          animation="blink 1s steps(1) infinite"
          sx={{
            "@keyframes blink": {
              "0%, 49%": { opacity: 1 },
              "50%, 100%": { opacity: 0 },
            },
          }}
        />
      )}
    </Box>
  );
};

export default RotatingText;
